import { useAtomCallback } from "jotai/utils";
import { useNavigate } from "react-router-dom";
import { AuthFlows, AuthSteps, FLOW_STEPS } from "../../constants/authSteps";
import { useAppSelector } from "../../store/hooks";
import {
  selectEmailVerified,
  selectPhoneAdded,
  selectPhoneVerified,
} from "../../store/selectors/userInfoSelectors";
import {
  getAdminDashboardRoute,
  getDashboardRoute,
  getProfileScreenRoute,
} from "../../store/utils/routeGetters";
import { emitAnalyticsTrackingEvent } from "../../utils/analyticsUtils";
import { useIsUMGUser } from "../useIsAandR";
import { currentSignUpStepAtom } from "../../atoms/location/currentSignUpStepAtom";
import { authModalOpenAtom } from "../../atoms/location/authModalOpenAtom";
import { currentFlowAtom } from "../../atoms/location/currentFlowAtom";
import { isAdmin } from "../useIsAdmin";
import { useMediaQueryBreakpoint } from "../useMediaQuery";

/**
 * @description
 * This will return the steps for the current flow and the function to navigate to the previous step.
 */
export const useAuthNavigationSteps = () => {
  const isEmailVerified = useAppSelector(selectEmailVerified);
  const isPhoneVerified = useAppSelector(selectPhoneVerified);
  const isPhoneAdded = useAppSelector(selectPhoneAdded);
  const { isMobile } = useMediaQueryBreakpoint();
  const { user } = useAppSelector((state) => state.accountInfo);
  const isUMGUser = useIsUMGUser(user);

  const navigate = useNavigate();

  return useAtomCallback((get, set) => {
    const currentFlow = get(currentFlowAtom);
    const currentStep = get(currentSignUpStepAtom);
    const flowSteps = FLOW_STEPS[currentFlow]?.steps.flat() || [];

    const nextStep = (options?: { skipVerification?: boolean }) => {
      const currentStepIndex = flowSteps.indexOf(currentStep);
      let targetStepIndex = currentStepIndex;

      if (currentStepIndex === flowSteps.length - 1) {
        if (isMobile) {
          navigate(getProfileScreenRoute(user?.username ?? ""));
        }
        set(authModalOpenAtom, false);
        return;
      }

      if (options?.skipVerification) {
        if (
          [
            AuthFlows.STUDIO_OWNER_RESUME_LOGIN,
            AuthFlows.AANDR_RESUME_LOGIN,
            AuthFlows.STANDARD_RESUME_LOGIN,
          ].includes(currentFlow)
        ) {
          set(authModalOpenAtom, false);
          const dashboardRoute = isAdmin(user)
            ? getAdminDashboardRoute()
            : getDashboardRoute();
          if (isMobile) navigate(dashboardRoute);
          return;
        }
        targetStepIndex = flowSteps.length - 1;
      } else {
        for (let i = currentStepIndex + 1; i < flowSteps.length; i++) {
          const nextStep = flowSteps[i];

          if (nextStep === AuthSteps.AANDR_LABEL_SELECTION && !isUMGUser) {
            continue;
          }

          if (nextStep === AuthSteps.VERIFY_EMAIL && isEmailVerified) {
            continue;
          }

          if (
            (nextStep === AuthSteps.ADD_PHONE ||
              nextStep === AuthSteps.AANDR_ADD_PHONE) &&
            isPhoneAdded
          ) {
            continue;
          }

          if (
            (nextStep === AuthSteps.VERIFY_PHONE ||
              nextStep === AuthSteps.AANDR_VERIFY_PHONE) &&
            isPhoneVerified
          ) {
            continue;
          }

          targetStepIndex = i;
          break;
        }
      }

      set(currentSignUpStepAtom, flowSteps[targetStepIndex]);
    };

    const prevStep = () => {
      emitAnalyticsTrackingEvent("auth_prev_step_clicked", {});
      const currentStepIndex = flowSteps.indexOf(currentStep);
      if (currentStepIndex > 0) {
        const prevStepIndex = currentStepIndex - 1;

        if (
          flowSteps[prevStepIndex] === AuthSteps.AANDR_LABEL_SELECTION &&
          !isUMGUser
        ) {
          set(currentSignUpStepAtom, flowSteps[prevStepIndex - 1]);
        } else if (flowSteps[prevStepIndex] === AuthSteps.VERIFY_EMAIL) {
          set(currentSignUpStepAtom, flowSteps[prevStepIndex - 2]);
        } else if (
          flowSteps[prevStepIndex] === AuthSteps.VERIFY_PHONE ||
          flowSteps[prevStepIndex] === AuthSteps.AANDR_VERIFY_PHONE
        ) {
          set(currentSignUpStepAtom, flowSteps[prevStepIndex - 2]);
        } else {
          set(currentSignUpStepAtom, flowSteps[prevStepIndex]);
        }
      }
    };
    return {
      nextStep,
      prevStep,
      flowSteps,
    };
  });
};
